import React, {useEffect, useState, useCallback} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Text,
  TouchableOpacity,
  View,
  ScrollView,
  Image
} from 'react-native';
import {useNavigation, useFocusEffect} from '@react-navigation/native';
import Header from '../../components/Header/Header';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import OrdersService from '../../services/OrdersService/OrdersService';
import SalesService from '../../services/SalesService/SalesService';
import ModalFilter from '../Home/components/ModalFilter/ModalFilter';
import OrderCard from './components/OrderCard/OrderCard';
import {styles} from './styles';
import NotOrder from './NotOrder/NotOrder';
import ErrorOrder from './ErrorOrder/ErrorOrder';
import { Colors } from '../../constants/Colors';
import ModalFilterOrders from './components/ModalFilterOrders';
import {Images} from '../../constants/Images';
import {AppRoutes} from '../../navigation/Routes.types';
import PendingPaymentCard from './components/Pendingpaymentcard';
import ScheduledProposalService, {
  AVistaOrderListItem,
} from '../../services/ScheduledProposalService/ScheduledProposalService';
import { useUserStore } from '../../store/UserStore';
import {NavigationProps} from '../../navigation/Routes';
import { useIsDesktop } from '../../hooks/useIsDesktop';

/** Mesmo formatador de `Pendingpaymentcard.tsx` — a seção vizinha da mesma
 * tela, para o dinheiro não trocar de cara de um card pro outro. */
const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(
    value,
  );

/**
 * PAG-01 (26/08/2026) — a linha de resumo do card. Com dois pedidos "Pedido a
 * pagar" lado a lado, sem o nome do produto os dois cards eram idênticos: o
 * cliente não tinha como saber qual é qual antes de tocar. `primeiroProduto`
 * vem do que SOBREVIVEU à resolução de preço no servidor — pode ser `null`
 * quando o catálogo falhou lá, e aí a contagem crua ainda diz algo.
 */
const describeAVistaItens = (pedido: AVistaOrderListItem): string => {
  if (!pedido.primeiroProduto) {
    return pedido.itens === 1 ? '1 item' : `${pedido.itens} itens`;
  }
  const resto = pedido.itens - 1;
  if (resto <= 0) return pedido.primeiroProduto;
  return `${pedido.primeiroProduto} e mais ${resto === 1 ? '1 item' : `${resto} itens`}`;
};

interface IResponseMyOrders {
  statusPedidos: Array<any>;
}

interface IPendingPayment {
  id: string;
  client_code: string;
  reference_type: string;
  reference_id: string;
  transaction_id: string;
  operation_id: string;
  status: string;
  pix_copy_paste: string;
  confirmed: boolean;
  amount: number;
  pix_key: string;
  pix_qrcode_url: string;
  statement_descriptor: string;
  expiration_date: string;
  expected_on: string;
  created_at: string;
  updated_at: string;
}

const Orders = () => {
  const isDesktop = useIsDesktop();
  const navigation = useNavigation();
  const {navigate} = useNavigation<any>();
  const [filterModal, setFilterModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [filterParams, setFilterParams] = useState<IFilterOrders>({
    sort: '',
  });

  const [ordersData, setOrdersData] = useState<IResponseMyOrders>();
  const [errorData, setErrorData] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(false);
  const [noData, setNoData] = useState<boolean>(false);
  const [noDataMessage, setNotMessage] = useState<string>(false);
  
  const { user } = useUserStore();

  // Estados para pagamentos pendentes
  const [pendingPayments, setPendingPayments] = useState<IPendingPayment[]>([]);
  const [loadingPendingPayments, setLoadingPendingPayments] = useState<boolean>(false);
  const [errorPendingPayments, setErrorPendingPayments] = useState<boolean>(false);

  const [filterModalOrders, setFilterModalOrders] = useState<boolean>(false);
  const [filter, setFilter] = useState<{ diasPedido: number; type: string; status: string }>({
    diasPedido: 15,
    type: 'all',
    status: 'all',
  });
  const [status, setStatus] = useState<Array<any>>([]);

  useEffect(() => {
    const fetchStatus = async () => {
      const myStatusOrders = await OrdersService.MyStatusOrdersPost();
      setStatus(myStatusOrders);
    };
    fetchStatus();
  }, []);

  const [avistaOrders, setAvistaOrders] = useState<AVistaOrderListItem[]>([]);

  // Buscar pagamentos pendentes
  const fetchPendingPayments = async () => {
    try {
      setLoadingPendingPayments(true);
      setErrorPendingPayments(false);
      const response = await SalesService.PendingPaymentOrders({client_code: user?.clientCode});
      
      if (Array.isArray(response)) {
        setPendingPayments(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setPendingPayments(response.data);
      } else {
        setPendingPayments([]);
      }
    } catch (error) {
      console.error('Erro ao buscar pagamentos pendentes:', error);
      setErrorPendingPayments(true);
      setPendingPayments([]);
    } finally {
      setLoadingPendingPayments(false);
    }
  };

  // PAG-01 (26/08/2026) — pedidos à vista A PAGAR. Saíram de "Pedidos
  // programados" (lá eles eram apresentados como sendo "para uma data futura",
  // e vencem hoje) e passaram a viver aqui, que é onde o cliente procura o que
  // tem a resolver agora.
  //
  // Sem estado de erro: falha aqui devolve `[]` e a seção some. Ela é
  // acessória — derrubar ou sinalizar erro na lista de pedidos por causa dela
  // seria trocar um problema pequeno por um grande. O erro fica no console,
  // como o das outras leituras desta tela.
  const fetchAVistaOrders = async () => {
    try {
      const clientCode = user?.current_client?.clientCode ?? user?.clientCode;
      if (!clientCode) {
        setAvistaOrders([]);
        return;
      }
      setAvistaOrders(await ScheduledProposalService.GetAVistaOrders(clientCode));
    } catch (error) {
      console.error('Erro ao buscar pedidos à vista:', error);
      setAvistaOrders([]);
    }
  };

  const MyOrders = async ({
    diasPedido = 15,
    type = 'all',
    status = 'all',
  }: { diasPedido?: number; type?: string; status?: string } = {}) => {
    try {
      setLoading(true);

      const response = await OrdersService.MyUpdatedOrdersPost(diasPedido, type, status);
      setOrdersData({ statusPedidos: [] });

      if (response?.details) {
        setNoData(true);
        setNotMessage(response?.details);
        return;
      }
      if (
        (!response?.statusPedidos || response?.statusPedidos?.length === 0) &&
        response?.pedidosNaoProcessados?.length === 0
      ) {
        setNoData(true);
        setNotMessage('Nenhum pedido encontrado');
        return;
      }
      if (Array.isArray(response?.statusPedidos)) {
        setOrdersData({ ...response, statusPedidos: response.statusPedidos });
      } else {
        response.statusPedidos = [response.statusPedidos];
        setOrdersData(response);
      }
    } catch (error) {
      setErrorData(true);
      setErrorMessage(
        error?.response?.data?.details || 'Erro ao buscar pedidos',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async ({diasPedido, type, status}: {diasPedido: number, type: string, status: string}) => {
    setRefreshing(true);
    await Promise.all([
      MyOrders({diasPedido, type, status}),
      fetchPendingPayments(),
      fetchAVistaOrders()
    ]);
    setRefreshing(false);
  };

  const handleGoToPayment = (payment: IPendingPayment) => {
    const pixResponse = {
      id: payment.id,
      transaction_id: payment.transaction_id,
      pix_copy_paste: payment.pix_copy_paste,
      pix_qrcode_url: payment.pix_qrcode_url,
      amount: payment.amount,
      expiration_date: payment.expiration_date,
      status: payment.status,
      reference_id: payment.reference_id,
    };
    
    navigation.navigate(AppRoutes.ORDER_SENT_PIX, pixResponse);
  };

  const renderItem = ({item, index}: {item: IStatusPedido; index: number}) => (
    <OrderCard
      key={index}
      item={item}
      onPress={() => null}
      returnSort={(val: string) => setFilterParams({sort: val})}
    />
  );

  useFocusEffect(
    useCallback(() => {
      // Limpa os dados ao abrir a página
      setOrdersData(undefined);
      setNoData(false);
      setErrorData(false);
      setLoading(true);
      setPendingPayments([]);
      setAvistaOrders([]);

      // Carrega os dados novamente
      Promise.all([
        MyOrders(filter),
        fetchPendingPayments(),
        fetchAVistaOrders()
      ]).finally(() => setLoading(false));
    }, [filter]),
  );

  /**
   * PAG-01 — "A pagar hoje". Some inteira quando não há nada, e é o caso comum.
   *
   * Vem ANTES de "Pagamentos Pendentes" de propósito: aquela lista cobranças
   * PIX JÁ GERADAS (o cliente começou a pagar e abandonou); esta lista o pedido
   * que ainda **nem tem cobrança** — é um passo antes no mesmo funil, e o
   * prazo dele morre hoje.
   */
  const renderAVistaOrdersSection = () => {
    if (avistaOrders.length === 0) {
      return null;
    }

    return (
      <View style={styles.avistaSection}>
        <Text style={styles.avistaTitle}>A pagar hoje</Text>
        <Text style={styles.avistaDescription}>
          {avistaOrders.length === 1
            ? 'Seu vendedor montou 1 pedido para pagamento à vista. Pague hoje até 23:59.'
            : `Seu vendedor montou ${avistaOrders.length} pedidos para pagamento à vista. Pague hoje até 23:59.`}
        </Text>
        {avistaOrders.map((pedido, index) => (
          <TouchableOpacity
            key={pedido.setId || index}
            style={styles.avistaCard}
            activeOpacity={0.8}
            onPress={() =>
              // Mesma assinatura que o menu de programados usava — a tela de
              // revisão e todo o fluxo de checkout são indiferentes a quem
              // chamou.
              navigation.navigate(AppRoutes.AVISTA_ORDER_REVIEW, {
                setId: pedido.setId,
                clientCode: pedido.clientCode,
              })
            }>
            <View style={styles.avistaCardHeader}>
              <Text style={styles.avistaCardTitulo}>Pedido a pagar</Text>
              {pedido.total !== null ? (
                <Text style={styles.avistaCardValor}>
                  {formatCurrency(pedido.total)}
                </Text>
              ) : null}
            </View>
            <Text style={styles.avistaCardItens} numberOfLines={1}>
              {describeAVistaItens(pedido)}
            </Text>
            <Text style={styles.avistaCardAcao}>Toque para ver e pagar</Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // Componente de seção de pagamentos pendentes
  const renderPendingPaymentsSection = () => {
    if (loadingPendingPayments && !refreshing) {
      return (
        <View style={styles.pendingPaymentsSection}>
        <Text style={styles.orders}>Pedidos</Text>
          <ActivityIndicator color={Colors.RedPallete.red} size={20} />
        </View>
      );
    }

    if (errorPendingPayments) {
      return null; // Ou exibir uma mensagem de erro discreta
    }

    if (pendingPayments.length === 0) {
      return null; // Não exibe a seção se não houver pagamentos pendentes
    }

    return (
      <View style={styles.pendingPaymentsSection}>
        <Text style={styles.sectionTitle}>Pagamentos Pendentes</Text>
        <Text style={styles.sectionDescription}>
          Você possui {pendingPayments.length} {pendingPayments.length === 1 ? 'pagamento pendente' : 'pagamentos pendentes'}
        </Text>
        {pendingPayments.map((payment, index) => (
          <PendingPaymentCard
            key={payment.id || index}
            item={payment}
            onPress={() => handleGoToPayment(payment)}
          />
        ))}
      </View>
    );
  };

  return (
    <View style={[styles.container, isDesktop && { marginBottom: 0, maxWidth: 800, alignSelf: 'center', width: '100%' }]}>
      <Header refreshing={refreshing} />
      {loading && !refreshing ? (
        <ActivityIndicator  color={Colors.RedPallete.red} size={20} />
      ) : (
        <FlatList
          data={ordersData?.statusPedidos}
          renderItem={renderItem ? renderItem : null}
          keyExtractor={(item, index) => 'key' + index}
          showsVerticalScrollIndicator={false}
          refreshing={refreshing}
          onRefresh={() => handleRefresh(filter)}
          ListEmptyComponent={
            loading ? (
              <ActivityIndicator  color={Colors.RedPallete.red} size={20} />
            ) : errorData ? (
              <ErrorOrder title={errorMessage} />
            ) : noData ? (
              <NotOrder title={noDataMessage} />
            ) : null
          }
          ListHeaderComponent={
            <>
              <View style={styles.containerHeader}>
                <SearchHeader onPress={() => {
                  navigate(AppRoutes.MAIN_TAB_SCREEN, { 
                    screen: 'Home',
                    params: {
                      screen: 'HomeScreen'
                    }
                  });
                }} title="Pedidos" />
                <Text style={styles.description}>
                  Histórico de pedidos de produtos Brasal Refrigerantes
                </Text>
              </View>
              {/* PAG-01 — pedidos à vista a pagar hoje (antes das cobranças
                  PIX já geradas: é um passo antes no mesmo funil). */}
              {renderAVistaOrdersSection()}

              {/* Seção de Pagamentos Pendentes */}
              {renderPendingPaymentsSection()}

              <View style={styles.containerFilter}>
                <Text style={styles.orders}>Pedidos</Text>
                <TouchableOpacity onPress={() => setFilterModalOrders(true)} style={styles.filterButton}>
                  <Text style={styles.filter}>Filtrar</Text>
                  <Image source={Images.FiltersIcon} />
                </TouchableOpacity>
              </View>
            </>
          }
        />
      )}
        <ModalFilterOrders
          visible={filterModalOrders}
          onRequestClose={() => setFilterModalOrders(false)}
          currentDiasPedido={filter.diasPedido}
          currentType={filter.type}
          currentStatus={filter.status}
          returnFilter={filter => {
              setFilter(filter);
          }}
          cleanFilter={() => {
              setFilter({diasPedido: 15, type: 'all', status: 'all'});
          }}
          hasType
          hasStatus
          hasDiasPedido
          status={status}
        />
      <ModalFilter
        hasBrand
        hasCategory
        hasNews
        hasOrder
        hasSize
        onPress={() => null}
        visible={filterModal}
        returnFilter={filter =>
          setFilterParams({
            ...filterParams,
            category: filter.category,
            brand: filter.brand,
          })
        }
        onRequestClose={() => setFilterModal(false)}
      />
    </View>
  );
};

export default Orders;